<template>
  <div class="h-auto px-2 lg:px-0 pb-20 w-[99%] mx-auto">
    <div class="flex items-center justify-between gap-4">
      <h1 class="font-display text-gray-900 font-bold text-basicTitle pb-10 mx-5 lg:mx-0">{{ $t('cashback').tTitle() }}</h1>
      <button type="button" class="font-body text-base font-semibold underline pb-9 lg:pb-5 mx-5 lg:mr-0" @click="$emit('show-how-it-works')">
        {{ $t('cashback_how_it_works').tTitle() }}
      </button>
    </div>

    <div class="grid grid-cols-1-default l150:grid-cols-[360px_1fr] gap-[30px] mb-6 items-stretch min-w-0">
      <div class="bg-white rounded-lg shadow-ts1 w-full h-full flex flex-col min-h-0 min-w-0">
        <div class="px-5 md:px-[30px] pt-6 pb-4 border-b border-gray-400 border-solid">
          <h2 class="font-display text-xl font-bold text-blue">{{ $t('cashback_bonus_wallet').tTitle() }}</h2>
        </div>
        <div class="px-5 md:px-[30px] py-6 flex-1 flex flex-col min-h-0">
          <div class="available-bonus-card rounded-lg p-5 relative flex flex-col h-full min-h-[152px] w-full">
            <img src="@/assets/img/cashback/available-bonus.svg" :alt="$t('cashback_available_bonus').tTitle()" class="w-[60px] h-[60px] z-[1]" />
            <div class="flex items-center gap-1.5 mt-5 z-[1]">
              <p class="font-body text-[14px] text-white font-medium">{{ $t('cashback_available_bonus').tTitle() }}</p>
              <button type="button" @click.stop="toggleTooltip('bonus')">
                <img src="@/assets/img/cashback/i-white.svg" :alt="$t('cashback_tooltip_info')" class="w-3 h-3" />
              </button>
            </div>
            <p class="font-body font-bold text-[28px] xl:text-[35px] text-white z-[1]">{{ $formatMoney(availableBonus, currencySymbols[walletCurrency]) }}</p>
            <div
              v-if="activeTooltip === 'bonus'"
              v-closable="{ handler: 'closeTooltip', isOpen: activeTooltip === 'bonus' }"
              class="absolute z-20 top-[60%] mt-2 w-[110%] xl:w-[300px] bg-white border border-gray-300 rounded-lg shadow-ts3 p-5 xl:px-5 xl:py-3.5 ltr:left-[-5%] rtl:right-[-5%] ltr:xl:left-[40%] rtl:xl:right-[40%]"
            >
              <p class="font-body text-base text-gray-900">{{ $t('cashback_tooltip_bonus') }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-ts1 w-full h-full flex flex-col min-h-0 min-w-0">
        <div class="px-5 md:px-[30px] pt-6 pb-4 border-b border-gray-400 border-solid">
          <h2 class="font-display text-xl font-bold text-blue">{{ $t('cashback_wallet').tTitle() }}</h2>
        </div>
        <div class="px-5 md:px-[30px] py-6 flex-1 flex flex-col min-h-0">
          <div class="grid grid-cols-1-default xl:grid-cols-3-default gap-6 flex-1 min-h-0 min-w-0">
            <div v-for="stat in stats" :key="stat.key" class="rounded-lg p-5 relative flex flex-col h-full min-h-[152px] min-w-0" :class="stat.bgClass">
              <button
                v-if="stat.key === 'redeemable'"
                type="button"
                class="absolute top-4 ltr:right-4 rtl:left-4 bg-green text-white text-sm font-bold rounded-md px-5 py-2 disabled:cursor-not-allowed"
                :class="{ '!bg-[#C4C4C4]': !canRedeem || redeeming }"
                :disabled="!canRedeem || redeeming"
                @click="redeem"
              >
                {{ redeeming ? '...' : $t('cashback_redeem').tTitle() }}
              </button>
              <img :src="stat.icon" :alt="stat.label" class="w-[60px] h-[60px]" />
              <div class="flex items-center gap-1.5 mt-5">
                <p class="font-body text-[14px] text-gray-900 font-medium">{{ stat.label }}</p>
                <button type="button" class=" " @click.stop="toggleTooltip(stat.key)">
                  <img src="@/assets/img/cashback/i-blue.svg" :alt="$t('cashback_tooltip_info')" class="w-3 h-3" />
                </button>
              </div>
              <p class="font-body font-bold text-[28px] xl:text-[35px] text-gray-900">{{ $formatMoney(stat.value, currencySymbols[walletCurrency]) }}</p>
              <div
                v-if="activeTooltip === stat.key"
                v-closable="{ handler: 'closeTooltip', isOpen: activeTooltip === stat.key }"
                class="absolute z-20 top-[60%] mt-2 w-[110%] xl:w-[300px] bg-white border border-gray-300 rounded-lg shadow-ts3 p-5 xl:px-5 xl:py-3.5"
                :class="
                  stat.key === 'redeemable'
                    ? 'ltr:left-[-5%] rtl:right-[-5%] ltr:xl:left-[-40%] rtl:xl:right-[-40%]'
                    : 'ltr:left-[-5%] rtl:right-[-5%] ltr:xl:left-[40%] rtl:xl:right-[40%]'
                "
              >
                <p class="font-body text-base text-gray-900">{{ stat.tooltip }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1-default l150:grid-cols-[360px_1fr] gap-[30px] items-stretch min-w-0">
      <div class="bg-white rounded-lg shadow-ts1 overflow-hidden w-full h-full flex flex-col min-h-0 min-w-0">
        <div class="px-5 md:px-[30px] pt-6 pb-4 border-b border-gray-400 border-solid">
          <h2 class="font-display text-xl font-bold text-blue">{{ $t('cashback_rates').tTitle() }}</h2>
        </div>
        <div class="px-5 md:px-[30px] py-6 md:flex-1 flex flex-col md:min-h-0">
          <p class="font-body text-base font-semibold text-gray-900 mb-4">{{ $t('cashback_rates_subtitle') }}</p>

          <div class="bg-blue-background rounded-lg px-6 py-8 gap-3 md:gap-5 flex-1 min-h-0 flex flex-col">
            <div v-for="tier in rateTiers" :key="tier.name" class="bg-white/40 rounded-lg p-4">
              <p class="font-body text-base font-bold text-gray-900">{{ tier.name }}</p>
              <div class="flex justify-between mt-2">
                <div class="flex flex-wrap gap-2">
                  <span v-for="tag in tier.tags" :key="tag" class="inline-block border border-gray-900 rounded px-3 py-0.5 text-sm text-gray-900 font-body font-semibold">
                    {{ tag }}
                  </span>
                </div>
                <p class="font-body font-bold text-base text-gray-900">{{ tier.fx }}</p>
              </div>
              <div class="flex items-end justify-between gap-4 mt-2">
                <p class="font-body text-sm text-gray-900">{{ $t('cashback_under_ib') }}</p>
                <div class="ltr:text-right rtl:text-left">
                  <p class="font-body font-bold text-[12px] text-gray-900 mt-1">{{ tier.xau }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-ts1 overflow-hidden w-full h-full flex flex-col min-h-0">
        <div class="px-5 md:px-[30px] pt-6 pb-4 border-b border-gray-400 border-solid">
          <h2 class="font-display text-xl font-bold text-blue">{{ $t('cashback_summary').tTitle() }}</h2>
        </div>
        <div class="px-5 md:px-[30px] py-6 flex-1 flex flex-col min-h-0">
          <div v-if="summaryRecords.length" class="pb-4 flex-1 min-h-0 overflow-x-auto">
            <table class="w-full border-collapse lg:table-fixed lg:min-w-[600px]">
              <thead class="hidden lg:table-header-group font-body text-blue font-bold">
                <tr>
                  <th class="w-2/5 ltr:text-left rtl:text-right ltr:pl-7 rtl:pr-7 text-base pb-4">{{ $t('cashback_request_date').tTitle() }}</th>
                  <th class="w-2/5 ltr:text-left rtl:text-right text-base pb-4">{{ $t('cashback_requested_amount').tTitle() }}</th>
                  <th class="w-1/5 ltr:text-left rtl:text-right text-base pb-4">{{ $t('status').tTitle() }}</th>
                </tr>
              </thead>
              <tbody class="font-body block lg:table-row-group">
                <tr
                  v-for="(record, index) in summaryRecords"
                  :key="'cashback-' + index"
                  class="block lg:table-row odd:bg-gray-100 even:bg-transparent lg:even:bg-white rounded-lg lg:rounded-none overflow-hidden lg:h-[50px]"
                  :class="record.expanded ? 'h-auto' : 'h-[50px]'"
                >
                  <td
                    class="flex lg:table-cell justify-between items-center gap-3 px-4 lg:ltr:pl-7 lg:rtl:pr-7 h-[50px] lg:h-auto lg:py-4 text-base text-gray-900 lg:ltr:rounded-l-md lg:rtl:rounded-r-md"
                  >
                    <button type="button" class="lg:hidden flex items-center font-bold text-blue shrink-0" @click="expandRecord(index)">
                      <img
                        width="12"
                        height="12"
                        class="ltr:mr-2 rtl:ml-2"
                        :class="{ 'ltr:-rotate-90 rtl:rotate-90': !record.expanded }"
                        src="@/assets/img/platform-dropdown.svg"
                        alt=""
                      />
                      {{ $t('cashback_request_date').tTitle() }}
                    </button>
                    <span>{{ $formatDate(record.date) }}</span>
                  </td>
                  <td class="flex lg:table-cell justify-between items-center gap-3 px-4 h-[50px] lg:h-auto lg:py-3 text-base text-gray-900">
                    <span class="lg:hidden font-bold text-blue ltr:pl-5 rtl:pr-5">{{ $t('cashback_requested_amount').tTitle() }}</span>
                    <span>{{ $formatMoney(record.amount, currencySymbols[walletCurrency]) }}</span>
                  </td>
                  <td class="flex lg:table-cell justify-between items-center gap-3 px-4 h-[50px] lg:h-auto lg:py-3 text-base lg:ltr:rounded-r-md lg:rtl:rounded-l-md">
                    <span class="lg:hidden font-bold text-blue ltr:pl-5 rtl:pr-5">{{ $t('status').tTitle() }}</span>
                    <span
                      class="min-w-[100px] lg:min-w-[130px] text-center inline-block text-sm lg:text-base font-body font-medium rounded-full px-5 lg:px-7 py-0.5"
                      :class="statusColorClass(record.status)"
                    >
                      {{ $t(record.status).tTitle() }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="py-8">
            <p class="text-center text-base text-blue font-semibold">{{ $t('no_records') }}</p>
          </div>

          <div class="flex flex-col l150:flex-row items-center md:relative mt-auto pt-4">
            <div class="text-gray-900 font-body text-sm leading-10 order-2 l150:order-none l150:pl-2">{{ pagination.total }} {{ $t('records') }}</div>
            <div class="flex justify-center order-1 l150:order-none l150:absolute l150:left-1/2 l150:-translate-x-1/2">
              <pagination-component
                v-if="pagination.total > pagination.rowsPerPage"
                :page="pagination.page"
                :maxPerPage="pagination.rowsPerPage"
                :total="pagination.total"
                :showNavOnMobile="true"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <p class="font-body text-base text-gray-900 maxS:px-4 mt-6 pl-1" v-html="$t('cashback_faq_hint', { URL: faqUrl })"></p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import PaginationComponent from '@/components/Pagination.vue'
import links from '@/config/links'
import config from '@/config'
import { service } from '../../main.js'

export default {
  components: {
    PaginationComponent,
  },
  props: {
    userBlocked: {
      type: Boolean,
      default: false,
    },
    userVerified: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTooltip: null,
      redeeming: false,
      availableBonus: 0,
      totalCashback: 0,
      totalPaid: 0,
      pendingCashback: 0,
      redeemableCashback: 0,
      summaryRecords: [],
      walletCurrency: 'USD',
      currencySymbols: config.currencies,
      pagination: {
        page: 1,
        total: 0,
        rowsPerPage: 5,
      },
      rateTiers: [],
      canRedeemFlag: false,
    }
  },
  computed: {
    ...mapGetters({
      Language: 'language/code',
      UserInfo: 'user/info',
    }),
    faqUrl() {
      return links.getLink('cashbackFAQsUrl', this.Language)
    },
    stats() {
      return [
        {
          key: 'total',
          label: this.$t('cashback_total_paid').tTitle(),
          value: this.totalPaid,
          tooltip: this.$t('cashback_tooltip_total'),
          icon: require('@/assets/img/cashback/total-cashback.svg'),
          bgClass: 'bg-blue/15',
        },
        {
          key: 'pending',
          label: this.$t('cashback_pending').tTitle(),
          value: this.pendingCashback,
          tooltip: this.$t('cashback_tooltip_pending'),
          icon: require('@/assets/img/cashback/pending-cashback.svg'),
          bgClass: 'bg-gold/15',
        },
        {
          key: 'redeemable',
          label: this.$t('cashback_redeemable').tTitle(),
          value: this.redeemableCashback,
          tooltip: this.$t('cashback_tooltip_redeemable'),
          icon: require('@/assets/img/cashback/cashback-redeemable.svg'),
          bgClass: 'bg-green/15',
        },
      ]
    },
    canRedeem() {
      return  !!this.canRedeemFlag && !this.userBlocked && this.userVerified && this.redeemableCashback > 0
    },
  },
  watch: {
    'pagination.page'() {
      this.loadSummary()
    },
  },
  created() {
    this.$bus.$on('pageChanged', this.onPageChanged)
  },
  beforeDestroy() {
    this.$bus.$off('pageChanged', this.onPageChanged)
  },
  mounted() {
    this.loadData()
  },
  methods: {
    getWalletBalance() {
      if (!this.UserInfo.ID) return

      service
        .get('/cashback/wallet/' + this.UserInfo.ID)
        .then(response => {
          this.totalCashback = response.data.total_cashback
          this.pendingCashback = response.data.pending_rollover_cashback
          this.redeemableCashback = response.data.redeemable_cashback
          this.availableBonus = response.data.cashback_cap - this.totalCashback
          this.walletCurrency = response.data.currency || 'USD'
          this.canRedeemFlag = !!response.data.can_redeem
        })
        .catch(error => {
          this.availableBonus = 0
          this.canRedeemFlag = false
          this.$toasted.error(this.$t(error.response?.data?.meta?.messageID), { duration: 10000 })
        })
    },
    getRates() {
      if (!this.UserInfo.ID) return

      service
        .post('/cashback/config', {
          userID: this.UserInfo.ID,
        })
        .then(response => {
          const data = response.data || {}
          const symbol = this.currencySymbols[this.walletCurrency]

          this.rateTiers = [
            {
              name: this.$t('cashback_micro_silver').tTitle(),
              tags: ['FX', 'XAUUSD', 'XAUEUR'],
              fx: this.$formatMoney(data.rate_micro_silver_no_ib, symbol),
              xau: this.$formatMoney(data.rate_micro_silver_under_ib, symbol),
            },
            {
              name: this.$t('cashback_gold_platinum').tTitle(),
              tags: ['FX', 'XAUUSD', 'XAUEUR'],
              fx: this.$formatMoney(data.rate_gold_platinum_no_ib, symbol),
              xau: this.$formatMoney(data.rate_gold_platinum_under_ib, symbol),
            },
          ]
        })
        .catch(error => {
          this.rateTiers = []
          this.$toasted.error(this.$t(error.response?.data?.meta?.messageID), { duration: 10000 })
        })
    },
    onPageChanged(page) {
      this.pagination.page = page
    },
    toggleTooltip(key) {
      this.activeTooltip = this.activeTooltip === key ? null : key
    },
    closeTooltip() {
      this.activeTooltip = null
    },
    loadData() {
      this.getWalletBalance()
      this.loadSummary()
      this.getRates()
    },
    loadSummary() {
      if (!this.UserInfo.ID) return

      service
        .post('/cashback/transactions', {
          userID: this.UserInfo.ID,
          page: this.pagination.page,
          limit: this.pagination.rowsPerPage,
        })
        .then(response => {
          const transactions = response.data.transactions || []
          this.summaryRecords = transactions.map(item => ({
            date: item.request_date || item.date,
            amount: item.amount,
            status: item.status,
            expanded: false,
          }))
          this.pagination.total = response.data.count || 0
          this.totalPaid = response.data && response.data.total_paid !== undefined ? Number(response.data.total_paid) : 0
        })
        .catch(error => {
          this.summaryRecords = []
          this.pagination.total = 0
          this.$toasted.error(this.$t(error.response?.data?.meta?.messageID), { duration: 10000 })
        })
    },
    expandRecord(index) {
      this.$set(this.summaryRecords, index, {
        ...this.summaryRecords[index],
        expanded: !this.summaryRecords[index].expanded,
      })
    },
    statusColorClass(status) {
      if (status === 'approved') return 'bg-green text-white'
      if (status === 'declined') return 'bg-orange text-white'
      if (status === 'pending') return 'bg-gold text-gray-900'
      return ''
    },
    redeem() {
      if (!this.canRedeem) return
      if (this.userBlocked || !this.userVerified) return this.$emit('redeem-result', 'unavailable')
      this.redeeming = true
      service
        .post('/cashback/redeem', {
          userID: this.UserInfo.ID,
          amount: this.redeemableCashback,
        })
        .then(response => {
          if (response.data.success) {
            this.$emit('redeem-result', 'success')
            this.getWalletBalance()
            this.loadSummary()
          } else {
            this.$emit('redeem-result', 'error')
          }
          this.redeeming = false
        })
        .catch(error => {
          this.redeeming = false
          this.$toasted.error(this.$t(error.response?.data?.meta?.messageID), { duration: 10000 })
          this.$emit('redeem-result', this.userBlocked ? 'unavailable' : 'error')
        })
    },
  },
}
</script>

<style scoped>
.available-bonus-card {
  background: #2b63e2;
}

.available-bonus-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to top right, #2b63e3 20%, #2d394b 105%);
  clip-path: polygon(0 0%, 0 98%, 105% 0%);
  pointer-events: none;
  border-radius: 8px;
}
</style>
